import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import '../index.css';

const CreateShowcase = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length + images.length > 5) {
      setError('Maximum 5 images allowed');
      return;
    }

    // Check file sizes
    for (let file of files) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Each image must be less than 10MB');
        return;
      }
    }

    setImages([...images, ...files]);
    
    // Create previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setImagePreviews([...imagePreviews, ...newPreviews]);
    setError('');
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setImages(newImages);
    setImagePreviews(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!title) {
      setError('Title is required');
      setLoading(false);
      return;
    }

    if (images.length === 0) {
      setError('At least one image is required');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('title', title);
      formData.append('description', description);
      
      images.forEach((image) => {
        formData.append('images', image);
      });

      await axios.post('/api/showcase', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      navigate(`/profile/${user.username}`);
    } catch (err) {
      console.error('Create showcase error:', err);
      setError(err.response?.data?.message || 'Failed to create showcase item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{
        background: `linear-gradient(135deg, var(--profile-gradient-start) 0%, var(--profile-gradient-end) 100%)`,
        borderRadius: '20px',
        padding: '40px',
        border: `1px solid var(--modal-border)`,
        boxShadow: 'var(--shadow-hover)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h1 style={{ margin: 0, fontSize: '2rem', color: 'var(--text-dark)' }}>🎨 Add to Showcase</h1>
          <button
            onClick={() => navigate(`/profile/${user?.username}`)}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--btn-secondary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Cancel
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--error-bg)',
            color: 'var(--error-text)',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: 'var(--text-dark)' }}>
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              placeholder="Give your work a title..."
              required
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid var(--border-color)`,
                backgroundColor: 'var(--info-box-bg)',
                color: 'var(--text-primary)',
                fontSize: '1rem'
              }}
            />
            <p style={{ marginTop: '5px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              {title.length}/100 characters
            </p>
          </div>

          {/* Description */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: 'var(--text-dark)' }}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
              rows={4}
              placeholder="Describe your work, skills, or techniques used..."
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid var(--border-color)`,
                backgroundColor: 'var(--info-box-bg)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
            <p style={{ marginTop: '5px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              {description.length}/1000 characters
            </p>
          </div>

          {/* Images */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: 'var(--text-dark)' }}>
              Images * (Max 5, up to 10MB each)
            </label>
            
            {imagePreviews.length < 5 && (
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: `1px solid var(--border-color)`,
                  backgroundColor: 'var(--info-box-bg)',
                  color: 'var(--text-primary)',
                  marginBottom: '15px'
                }}
              />
            )}

            {imagePreviews.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '15px',
                marginTop: '15px'
              }}>
                {imagePreviews.map((preview, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      paddingTop: '100%',
                      backgroundColor: 'var(--info-box-bg)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #555'
                    }}
                  >
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        padding: '6px 10px',
                        backgroundColor: 'rgba(255, 0, 0, 0.8)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '15px',
              background: loading ? '#ccc' : 'linear-gradient(135deg, #9C27B0, #7B1FA2)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              boxShadow: loading ? 'none' : '0 4px 15px rgba(156, 39, 176, 0.3)'
            }}
          >
            {loading ? 'Creating...' : '✨ Create Showcase'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateShowcase;
